import { Brand, I18Next, Version, factbird } from '@blackbird/ui-base';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { FC, useEffect } from 'react';
import Rollbar from 'rollbar';
import { GlobalStyles as TSSGlobalStyles } from 'tss-react';

import PageLayout from '@/components/layout/page-layout';
import createEmotionCache from '@/emotion-cache';

const clientSideEmotionCache = createEmotionCache();

interface AppProps extends NextAppProps {
  emotionCache?: EmotionCache;
}

const App: FC<AppProps> = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps = {} } = props;

  useEffect(() => {
    new Rollbar({
      accessToken: process.env.ROLLBAR_CLIENT_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      enabled: process.env.NODE_ENV === 'production',
      payload: {
        environment: process.env.NODE_ENV,
        client: {
          javascript: {
            source_map_enabled: true,
            code_version: process.env.GIT_REVISION,
            guess_uncaught_frames: true,
          },
        },
      },
    });

    const jssStyles = document.querySelector('#jss');
    if (jssStyles) {
      jssStyles.parentElement?.removeChild(jssStyles);
    }
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>Sign In | Factbird</title>
        <meta name="description" content="Log in to the Factbird Cloud Application" />
      </Head>
      <ThemeProvider theme={factbird.light}>
        <CssBaseline />
        <TSSGlobalStyles
          styles={{
            '@page': {
              size: 'auto',
              margin: 0,
            },
            html: {
              backgroundColor: factbird.dark.palette.background.default,
              '@media print': {
                backgroundColor: 'white',
              },
            },
          }}
        />

        <Version.default>
          <PageLayout>
            <Component {...pageProps} />
          </PageLayout>
        </Version.default>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default Brand.HOC(I18Next(App));
